import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Header } from "@/components/header";
import { checkAuthStatus } from "@/utils/auth";
import { Footer } from "@/components/footer";
import { Partners } from "@/components/partners";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link, useLocation } from "wouter";
import axios from "axios";
import { Loader2, Lock, Mail } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Toast, ToastProvider, ToastViewport } from "@/components/ui/toast";
import { useAuth } from "@/context/AuthContext";

// SSO Config
const AUTH_URL = import.meta.env.VITE_SSO_AUTH_URL;
const DASHBOARD_URL = import.meta.env.VITE_DASHBOARD_URL as string;
const LANDING_CLIENT_ID = import.meta.env.VITE_LANDING_CLIENT_ID as string;

interface FormErrors {
  email?: string;
  password?: string;
  general?: string;
}

export default function LoginPage() {
  const { t } = useTranslation();
  const { isAuthenticated, setIsAuthenticated } = useAuth();
  const [, navigate] = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState<FormErrors>({});

  // Toast
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  // === CHECK AUTH ON MOUNT ===
  useEffect(() => {
    const check = async () => {
      const isAuth = await checkAuthStatus('login-mount');
      if (isAuth) {
        setIsAuthenticated(true, 'login-mount');
        navigate('/');
      }
    };
    check();
  }, [setIsAuthenticated]);

  // === REDIRECT IF ALREADY LOGGED IN ===
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    // Clear field error on change
    if (errors[name as keyof FormErrors]) {
      setErrors((p) => ({ ...p, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!form.email) {
      newErrors.email = t('auth.errors.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      newErrors.email = t('auth.errors.invalidEmail');
    }

    if (!form.password) {
      newErrors.password = t('auth.errors.passwordRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || submitting) return;

    setSubmitting(true);
    setErrors({});

    try {
      await axios.post(
        `${AUTH_URL}/auth/oauth/login`,
        {
          identifier: form.email,
          password: form.password,
          client_id: LANDING_CLIENT_ID,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          withCredentials: true,
        }
      );

      // Success: Mark as authenticated + redirect
      setIsAuthenticated(true, 'login-success');
      localStorage.setItem('auth-status', 'true');
      navigate('/'); // Immediate redirect

    } catch (err: any) {
      let message = t('auth.errors.loginFailed');

      if (err.response?.status === 401) {
        message = t('auth.errors.invalidCredentials');
      } else if (err.response?.status === 403) {
        message = t('auth.errors.accessDenied');
      } else if (err.response?.data?.message) {
        message = err.response.data.message;
      } else if (!err.response) {
        message = t('auth.errors.serverError');
      }

      setErrors({ general: message });
    } finally {
      setSubmitting(false);
    }
  };

  // Show toast on general error
  useEffect(() => {
    if (errors.general) {
      setToastMessage(errors.general);
      setToastOpen(true);
    }
  }, [errors.general]);

  return (
    <div className="flex flex-col min-h-screen bg-white dark:bg-slate-900">
      <ToastProvider>
        <ToastViewport className="fixed top-6 left-1/2 -translate-x-1/2 z-50 w-full max-w-md px-4" />
        <Toast
          open={toastOpen}
          onOpenChange={setToastOpen}
          className="animate-in slide-in-from-top-8 fade-in-0 data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom-8 data-[state=closed]:fade-out-0 duration-600"
        >
          <div className="flex items-center gap-2 bg-red-50 dark:bg-red-900/20 text-red-900 dark:text-red-100 p-4 rounded-md shadow-lg">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm font-medium">{toastMessage}</p>
          </div>
        </Toast>
      </ToastProvider>

      <Header isLoggedIn={isAuthenticated} onDashboardClick={() => window.location.href = DASHBOARD_URL} />
      
      <main className="flex-grow py-8 md:py-12 lg:py-16 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <Card className="border border-slate-200 dark:border-slate-700 shadow-xl bg-white dark:bg-slate-800">
            <CardHeader className="space-y-1 text-center">
              <div className="mx-auto bg-slate-100 dark:bg-slate-700 p-3 rounded-full w-16 h-16 flex items-center justify-center mb-2">
                <Lock className="w-8 h-8 text-slate-600 dark:text-slate-300" />
              </div>
              <CardTitle className="text-2xl font-bold">
                {t("auth.login.welcomeBack")}
              </CardTitle>
              <CardDescription>
                {t("auth.login.enterCredentials")}
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{t("auth.login.email")}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={handleChange}
                      className={`pl-10 h-11 ${errors.email ? 'border-red-500' : ''}`}
                      placeholder="name@example.com"
                    />
                  </div>
                  {errors.email && <p className="text-sm text-red-500">{errors.email}</p>}
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label htmlFor="password">{t("auth.login.password")}</Label>
                    <Link href="/forgot-password" className="text-sm font-medium hover:underline">
                      {t("auth.login.forgotPassword")}
                    </Link>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      value={form.password}
                      onChange={handleChange}
                      className={`pl-10 h-11 ${errors.password ? 'border-red-500' : ''}`}
                      placeholder="••••••••"
                    />
                  </div>
                  
                  {errors.password && <p className="text-sm text-red-500">{errors.password}</p>}
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full h-11 mt-6 bg-slate-900 hover:bg-slate-800 text-white"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t("auth.login.signingIn")}
                    </>
                  ) : (
                    t("auth.login.signIn")
                  )}
                </Button>
              </form>
            </CardContent>

            <CardFooter className="flex flex-col items-center space-y-4 pt-0">
              <p className="text-sm text-muted-foreground text-center">
                {t("auth.login.noAccountPrefix")}{" "}
                <Link href="/contact-sales" className="font-medium hover:underline">
                  {t("auth.login.contactCommercial")}
                </Link>
              </p>
              <p className="text-xs text-muted-foreground text-center">
                {t("auth.login.terms")}{" "}
                <Link href="/terms" className="font-medium hover:underline">
                  {t("auth.login.termsLink")}
                </Link>{" "}
                {t("auth.login.and")}{" "}
                <Link href="/privacy" className="font-medium hover:underline">
                  {t("auth.login.privacyLink")}
                </Link>
              </p>
            </CardFooter>
          </Card>
        </div>
      </main>

      <Partners />
      <Footer />
    </div>
  );
}